import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import {
  Button,
  Col,
  Container,
  Row,
} from "reactstrap";
import { TEMPLATES_SOCIAL_FORM, TEMPLATE_SOCIAL_FORM } from "../../../../constants/modal.constant";
import { useGlobalCore } from "../../../../cores/globals";
import { Position } from "../../../../cores/globals/interfaces/globalEntity";
import { useMeCore } from "../../../../cores/me";
import { useSocialPostCore } from "../../../../cores/socialPost";
import { useTemplateCore } from "../../../../cores/templates";
import { RadiobuttonWembii } from "../../RadiobuttonWembii";
import "./TemplatesForm.scss";

const TemplateType = {
  all: 0,
  global: 1,
  user: 2
}

export default function TemplatesForm() {
  const intl = useIntl();
  const { clear, addText } = useSocialPostCore();
  const { socialTemplates } = useTemplateCore();
  const {
    company,
  } = useMeCore();
  const { turnOnModal, turnOffModal } = useGlobalCore();
  const [selected, setSelected] = useState(0);
  const [templateType, setTemplateType] = useState(TemplateType.all);

  const onSelecting = useCallback((template) => {
    clear();
    addText(template.message);
    turnOffModal(TEMPLATES_SOCIAL_FORM);
  },[clear, addText, turnOffModal]);

  const onChangingFilter = useCallback((type) => {
    setSelected(0);
    setTemplateType(type);
  },[]);

  const openTemplateModal = useCallback(() => {
    turnOffModal(TEMPLATES_SOCIAL_FORM);
    turnOnModal(TEMPLATE_SOCIAL_FORM, true, Position.LEFT);
  }, [turnOnModal, turnOffModal]);

  const templatesList = useMemo(() => 
    (templateType === TemplateType.all)
      ? socialTemplates
      : (templateType === TemplateType.global)
        ? socialTemplates.filter((template) => template.global)
        : socialTemplates.filter((template) => !template.global)
  , [socialTemplates, templateType]);

  const template = useMemo(() => 
    templatesList[selected] || null
  , [templatesList, selected]);

  const increase = useCallback(() => {
    if (selected < templatesList.length - 1) {
      setSelected(selected + 1);
    }
  }, [selected, templatesList]);

  const decrease = useCallback(() => {
    if (selected > 0) {
      setSelected(selected - 1);
    }
  }, [selected]);

  return (
    <Container className="templates-form h-100" fluid>
      <Row className="h-100">
        <Col xs="12">
          <div className="d-flex flex-column justify-content-between h-100">
            {/* Header */}
            <div className="templates-form-header">
              <h2>
                {intl.formatMessage({ id: "app.socialStory.template" })}
              </h2>
            </div>

            {/* Body */}
            <div className="templates-form-body">
              {/* Filters */}
              <div className="template-filters">
                <div 
                  className={`filter-option ${templateType === TemplateType.all ? 'active' : ''}`}
                  onClick={() => onChangingFilter(TemplateType.all)}
                >
                  <input 
                    type="radio" 
                    checked={templateType === TemplateType.all}
                    onChange={() => onChangingFilter(TemplateType.all)}
                    style={{ display: 'none' }}
                  />
                  {intl.formatMessage({ id: "app.socialStory.template.all" })}
                </div>
                <div 
                  className={`filter-option ${templateType === TemplateType.global ? 'active' : ''}`}
                  onClick={() => onChangingFilter(TemplateType.global)}
                >
                  <input 
                    type="radio" 
                    checked={templateType === TemplateType.global}
                    onChange={() => onChangingFilter(TemplateType.global)}
                    style={{ display: 'none' }}
                  />
                  {intl.formatMessage({ id: "app.socialStory.template.recommended" })}
                </div>
                <div 
                  className={`filter-option ${templateType === TemplateType.user ? 'active' : ''}`}
                  onClick={() => onChangingFilter(TemplateType.user)}
                >
                  <input 
                    type="radio" 
                    checked={templateType === TemplateType.user}
                    onChange={() => onChangingFilter(TemplateType.user)}
                    style={{ display: 'none' }}
                  />
                  {company?.name}
                </div>
              </div>

              {/* Template Content */}
              <div className="template-content">
                {template ? (
                  <>
                    <div className="template-title">{template.name}</div>
                    <div className="template-message">{template.message}</div>
                    <div className="template-counter">
                      {selected + 1} / {templatesList.length}
                    </div>
                  </>
                ) : (
                  <div className="template-title text-muted">
                    No existe ningún template
                  </div>
                )}
              </div>

              {/* Navigation */}
              <div className="template-navigation">
                <button 
                  className="nav-button"
                  onClick={decrease}
                  disabled={selected === 0}
                >
                  <FontAwesomeIcon icon={["fas", "angle-left"]} />
                </button>

                <div className="template-actions">
                  {template && (
                    <Button 
                      className="btn btn-primary" 
                      onClick={() => onSelecting(template)}
                    >
                      {intl.formatMessage({ id: "app.socialStory.template.select" })}
                    </Button>
                  )}
                </div>

                <button 
                  className="nav-button"
                  onClick={increase}
                  disabled={selected >= templatesList.length - 1}
                >
                  <FontAwesomeIcon icon={["fas", "angle-right"]} />
                </button>
              </div>
              
              {/* Manage Button */}
              <div className="template-manage">
                <Button
                  className="btn btn-white"
                  block
                  onClick={openTemplateModal}
                >
                  {intl.formatMessage({ id: "app.socialStory.template.manage" })}
                </Button>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
}
